/**
 * Phase and group-delay geometry. The backend returns wrapped phase
 * (`np.angle`, radians in (-pi, pi]) alongside the magnitude it's already
 * computed for the frequency-response chart — see FreqResponseResponse.phase.
 * Both charts here unwrap it first: a raw wrapped-phase plot is dominated by
 * sawtooth jumps at every ±pi crossing that carry no information, which is
 * why every acoustic measurement tool (REW included) unwraps before display.
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, logFreqToX, type LinearScale, type LogFreqScale } from './scales';
import { dbViewportFromSeries, mapSeriesToPlot, yDomainFromSeries, type PlotPoint } from './series';
import { linearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';

/** Unwraps a wrapped-phase sequence (radians) so consecutive samples never
 *  jump by more than pi — equivalent to `numpy.unwrap`. */
export function unwrapPhase(phaseRad: number[]): number[] {
  if (!phaseRad.length) return [];
  const out: number[] = [phaseRad[0]!];
  let offset = 0;
  for (let i = 1; i < phaseRad.length; i++) {
    let diff = phaseRad[i]! - phaseRad[i - 1]!;
    while (diff > Math.PI) { offset -= 2 * Math.PI; diff -= 2 * Math.PI; }
    while (diff < -Math.PI) { offset += 2 * Math.PI; diff += 2 * Math.PI; }
    out.push(phaseRad[i]! + offset);
  }
  return out;
}

/** Group delay, ms: tau(f) = -1/(2*pi) * dphi/df, central difference (forward/
 *  backward at the endpoints). Works on the non-uniform log-spaced frequency
 *  grid the backend uses — differentiates against actual Hz spacing, not bin
 *  index. `phaseRad` must already be unwrapped (see `unwrapPhase`). */
export function computeGroupDelayMs(freqs: number[], unwrappedPhaseRad: number[]): number[] {
  const n = Math.min(freqs.length, unwrappedPhaseRad.length);
  const out: number[] = new Array(n).fill(NaN);
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - 1);
    const hi = Math.min(n - 1, i + 1);
    if (hi === lo) continue;
    const dPhi = unwrappedPhaseRad[hi]! - unwrappedPhaseRad[lo]!;
    const dF = freqs[hi]! - freqs[lo]!;
    if (!(dF > 0)) continue;
    out[i] = (-1000 * dPhi) / (2 * Math.PI * dF);
  }
  return out;
}

const RAD_TO_DEG = 180 / Math.PI;

export interface PhaseInput {
  freqs: number[];
  /** Wrapped radians, as returned by the backend (np.angle). */
  phase: number[];
  phaseBaseline?: number[] | null;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  /** Display unit. Degrees is the acoustic-measurement-tool default. */
  unit?: 'deg' | 'rad';
  measureWidth?: (label: string) => number;
}

export interface PhaseGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  unit: 'deg' | 'rad';
  xScale: LogFreqScale;
  yScale: LinearScale;
  activeUnwrapped: number[];
  baselineUnwrapped: number[] | null;
  activePoints: PlotPoint[];
  baselinePoints: PlotPoint[] | null;
  xTicks: AxisTicks;
  yTicks: YTick[];
}

export function computePhaseGeometry(opts: PhaseInput): PhaseGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 200;
  const unit = opts.unit ?? 'deg';
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);
  const toDisplay = (rad: number[]) => (unit === 'deg' ? rad.map((v) => v * RAD_TO_DEG) : rad);

  if (!opts.freqs.length || !opts.phase.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, unit, xScale,
      yScale: createLinearScale(frame.plotH, -180, 180, true),
      activeUnwrapped: [], baselineUnwrapped: null, activePoints: [], baselinePoints: null,
      xTicks, yTicks: [],
    };
  }

  const activeUnwrapped = toDisplay(unwrapPhase(opts.phase));
  const baselineUnwrapped = opts.phaseBaseline?.length === opts.freqs.length
    ? toDisplay(unwrapPhase(opts.phaseBaseline))
    : null;

  const yDomain = yDomainFromSeries(
    baselineUnwrapped
      ? [{ x: opts.freqs, y: activeUnwrapped }, { x: opts.freqs, y: baselineUnwrapped }]
      : [{ x: opts.freqs, y: activeUnwrapped }],
  );
  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);

  const activePoints = mapSeriesToPlot({ x: opts.freqs, y: activeUnwrapped }, xScale, yScale);
  const baselinePoints = baselineUnwrapped
    ? mapSeriesToPlot({ x: opts.freqs, y: baselineUnwrapped }, xScale, yScale)
    : null;

  const rawStep = (yDomain.hi - yDomain.lo) / 6;
  const step = unit === 'deg'
    ? Math.max(45, Math.round(rawStep / 45) * 45)
    : Math.max(0.5, Math.round(rawStep / 0.5) * 0.5);
  const yTicks: YTick[] = linearTicks(yDomain.lo, yDomain.hi, step).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: Math.round(v) % (2 * step) === 0,
    label: unit === 'deg' ? `${Math.round(v)}` : `${v.toFixed(1)}`,
  }));

  return {
    empty: false, frame, fMin, fMax, unit, xScale, yScale,
    activeUnwrapped, baselineUnwrapped, activePoints, baselinePoints, xTicks, yTicks,
  };
}

export interface GroupDelayInput {
  freqs: number[];
  /** Wrapped radians (np.angle) — unwrapped internally before differentiating. */
  phase: number[];
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

export interface GroupDelayGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  groupDelayMs: number[];
  activePoints: PlotPoint[];
  xTicks: AxisTicks;
  yTicks: YTick[];
}

export function computeGroupDelayGeometry(opts: GroupDelayInput): GroupDelayGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 200;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  if (!opts.freqs.length || !opts.phase.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, xScale,
      yScale: createLinearScale(frame.plotH, 0, 10, true),
      groupDelayMs: [], activePoints: [], xTicks, yTicks: [],
    };
  }

  const groupDelayMs = computeGroupDelayMs(opts.freqs, unwrapPhase(opts.phase));
  // Percentile-clipped window (reused from the FR chart's dB windowing —
  // the math is generic outlier-clipping, not dB-specific) so one sharp
  // resonance spike doesn't flatten the rest of the curve to a line.
  const finite = groupDelayMs.filter(Number.isFinite);
  const yDomain = finite.length
    ? dbViewportFromSeries([finite], { signalFloor: -Infinity, roundTo: 1, minSpanDb: 2 })
    : { lo: 0, hi: 10 };
  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);

  const activePoints = mapSeriesToPlot({ x: opts.freqs, y: groupDelayMs }, xScale, yScale);

  const yTicks: YTick[] = linearTicks(yDomain.lo, yDomain.hi, Math.max(1, Math.round((yDomain.hi - yDomain.lo) / 5))).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: false, label: v.toFixed(1),
  }));

  return { empty: false, frame, fMin, fMax, xScale, yScale, groupDelayMs, activePoints, xTicks, yTicks };
}
